/**
 * Uplifted WHOLE from `apps/web/src/helper/env-helper.ts` (12 lines, both functions).
 *
 * WEB-ONLY — mobile has no equivalent — so this folder has a single `index.desktop.ts` and no
 * `shared.ts`, matching `helpers/feedbackHelper`. The whole file came across because its only
 * app importer was `data/env-constants`, and `isBetaProdBuild` calls `getDomainName`, so there
 * was no smaller closure to take.
 *
 * 🔴 **Reads the real `window` global, NOT the effects bridge's `getWindow()` — deliberately,
 * and this is one of the two places on the branch where that is correct** (the other is
 * `helpers/commonHelpers`'s `isMobileView`, for the same reason — see hazard 34). `data/envConstants` calls
 * `isBetaProdBuild()` at MODULE SCOPE, and the bridges are wired AFTER core modules evaluate
 * (ledger hazard 18). Through `getWindow()` this would read `undefined` at every call that
 * matters, `isBetaProdBuild()` would return `false` forever, and `currentAppEnv` would silently
 * resolve to `"prod"` on beta builds instead of `"beta"` — a wrong answer no typechecker or
 * bundler can see.
 *
 * That is safe here for a reason that does NOT generalise: this file is `.desktop`, so Metro
 * never loads it, and under webpack it always evaluates inside a browser where `window` is a
 * real global. `utils/nativeHooks/index.desktop.ts` is web-only too and still uses
 * `getWindow()` — because it reads window inside a FUNCTION, at call time, where the bridge
 * works. The rule is about WHEN the read happens, not which platform reads it.
 */

export function getDomainName(): string {
  const { hostname } = window.location;
  return hostname;
}

export function isBetaProdBuild(): boolean {
  // `window.electronAPI` is typed by a `declare global` block in `apps/web/src/index.tsx`,
  // which is in the APP's tsconfig program, not `packages/core`'s — so the property is
  // untyped here. Cast rather than replicate that `declare global`: a global augmentation in
  // core would also land in apps/mobile's program and give React Native a `Window.electronAPI`
  // that does not exist. `(window as any).electronAPI` is the same idiom apps/web already
  // uses at `helper/cartAuditHelper.ts:123` and `helper/localServer.ts:108`. Type-only change.
  if ((window as any).electronAPI?.env === "beta") return true;
  const currentDomain = getDomainName();
  if (currentDomain === process.env.REACT_APP_BILLING_BETA_DOMAIN_NAME) return true;
  return false;
}
